import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SEOData, generateTitle, generateCanonicalUrl, generateOGImage, SITE_URL, SITE_NAME } from "../utils/seo";

interface SEOHeadProps extends SEOData {
  structuredData?: object | object[];
}

/**
 * Компонент для управления SEO мета-тегами и структурированными данными
 */
export function SEOHead({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  author,
  publishedTime,
  modifiedTime,
  noindex = false,
  structuredData,
}: SEOHeadProps) {
  const location = useLocation();
  const currentUrl = url || generateCanonicalUrl(location.pathname);
  const pageTitle = generateTitle(title);
  const pageDescription = description || "Интерактивный SQL тренажёр для изучения ClickHouse. Практикуйтесь на реальных задачах, изучайте курсы и улучшайте свои навыки работы с данными.";
  const ogImage = generateOGImage(image);

  // Обрабатываем структурированные данные
  const structuredDataArray = Array.isArray(structuredData) 
    ? structuredData 
    : structuredData 
      ? [structuredData] 
      : [];

  return (
    <Helmet>
      {/* Основные мета-теги */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:locale" content="ru_RU" />
      <meta property="og:site_name" content={SITE_NAME} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Структурированные данные (JSON-LD) */}
      {structuredDataArray.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
}
